import React, { PureComponent, useState, useEffect } from "react";
import { Row, Col } from "react-bootstrap";
import { useFormik } from "formik";
import { FiArrowRight } from "react-icons/fi";
import Recaptcha from "react-recaptcha";
import * as Yup from "yup";

interface IContactUsFormProps {}

const PORTAL_ID = 4939140;
const FORM_GUID = "ddc1358e-0002-46c7-8fd9-dc8c5d396077";

export default function ContactUsForm() {
  const [formSubmitted, setFormSubmitted] = useState(false);
  const phoneRegExp = /^((\\+[1-9]{1,4}[ \\-]*)|(\\([0-9]{2,3}\\)[ \\-]*)|([0-9]{2,4})[ \\-]*)*?[0-9]{3,4}?[ \\-]*[0-9]{3,4}?$/;

  const validationSchema = Yup.object().shape({
    area_of_interest: Yup.string().required(
      "Please select an area of interest"
    ),
    full_name: Yup.string().required("Please enter your full name"),
    email: Yup.string()
      .email("Please enter a valid email addresss")
      .required("Please enter your email address"),
    mobilephone: Yup.string().matches(
      phoneRegExp,
      "Please enter a valid phone number"
    ),
    message: Yup.string().required("What would you like to talk to us about?"),
    // recaptcha: Yup.string().required("Recaptcha Required"),
  });

  useEffect(() => {
    const inputs = document.querySelectorAll<HTMLInputElement>(".input");

    if (inputs) {
      for (const input in inputs) {
        if (inputs.hasOwnProperty(input)) {
          const control: HTMLInputElement = inputs[input];

          control.addEventListener("focus", function() {
            control.parentElement.classList.add("focus");
          });

          control.addEventListener("focusout", function() {
            if (!control.value) {
              control.parentElement.classList.remove("focus");
            }
          });
        }
      }
    }
  });

  function encode(data) {
    return Object.keys(data)
      .map(key => encodeURIComponent(key) + "=" + encodeURIComponent(data[key]))
      .join("&");
  }

  const onSubmit = async (values, { setSubmitting }) => {
    setSubmitting(true);
    // convert formcontrolls to Array filter out captcha result
    const fields = Object.keys(values)
      .filter(i => (i === "recaptcha" ? false : true))
      .map(i => ({ name: i, value: values[i] }));

    // tslint:disable-next-line: no-unused-expression
    let netlifyFormSubmission;
    try {
      netlifyFormSubmission = await fetch("/", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: encode({
          "form-name": "contact",
          ...values,
        }),
      });
    } catch (error) {
      formik.setErrors({
        api: `The was an error with your form submission, please try again!`,
      });
    }

    const payload = {
      fields,
      context: {
        // hutk: ":hutk", // include this parameter and set it to the hubspotutk cookie value to enable cookie tracking on your submission
        pageUri: window.location.href,
        pageName: "Contact us",
      },
      skipValidation: true,
    };

    try {
      const hubspotFormSubmission = await fetch(
        `https://api.hsforms.com/submissions/v3/integration/submit/${PORTAL_ID}/${FORM_GUID}`,
        {
          method: "POST", // or 'PUT'
          body: JSON.stringify(payload), // data can be `string` or {object}!
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
        }
      );
    } catch (error) {
      formik.setErrors({
        api: `The was an error with your request to hubspot, please try again!`,
      });
    }

    setSubmitting(false);

    if (netlifyFormSubmission) {
      window.dataLayer = window.dataLayer || [];

      window.dataLayer.push({
        'event': 'GaForm',
        'EventCategory': 'form',
        'EventAction': 'submit',
        'EventLabel': 'Contact us'
      });
      setFormSubmitted(true);
    }
  };

  const formik = useFormik({
    initialValues: {
      'bot-field': "",
      area_of_interest: "",
      full_name: "",
      company: "",
      email: "",
      mobilephone: "",
      message: "",
      recaptcha: "",
    },
    validationSchema,
    onSubmit,
  });

  return formSubmitted ? (
    <Row className="d-flex flex-column">
      <Col className="d-flex flex-column justify-content-center">
        <h3>Thanks for contacting us</h3>
        <p className="text-accent">We'll be in touch soon </p>
      </Col>
    </Row>
  ) : (
    <div className="formBox">
      <form onSubmit={formik.handleSubmit} data-netlify="true" netlify-honeypot="bot-field" name="contact">
        <input type="hidden" name="form-name" value="contact" />
        <p className="hidden" hidden>
          <label>Don't fill this out if you're human: <input name="bot-field" value={formik.values['bot-field']} /></label>
        </p>
        <Row>
          <Col>
            <div className="inputBox form-group">
              <select
                className={
                  "input form-control " +
                  (formik.errors.area_of_interest &&
                  formik.touched.area_of_interest
                    ? "is-invalid"
                    : "")
                }
                value={formik.values.area_of_interest}
                onChange={formik.handleChange}
                name="area_of_interest"
              >
                <option hidden>Area of interest</option>
                <option value="Start a project">Start a project</option>
                <option value="General Enquiry">General Enquiry</option>
                <option value="UX Enquiry">UX Enquiry</option>
                <option value="Job Enquiry">Job Enquiry</option>
              </select>
              <div className="invalid-feedback">
                {formik.errors.area_of_interest}
              </div>
            </div>
          </Col>
        </Row>
        <Row>
          <Col sm={12}>
            <div className="inputBox input-group">
              <div className="inputText ">Full Name</div>
              <input
                type="text"
                name="full_name"
                value={formik.values.full_name}
                onChange={formik.handleChange}
                className={
                  "input form-control " +
                  (formik.errors.full_name && formik.touched.full_name
                    ? "is-invalid"
                    : "")
                }
              />
              <div className="invalid-feedback">{formik.errors.full_name}</div>
            </div>
          </Col>

          <Col sm={12}>
            <div className="inputBox input-group">
              <div className="inputText">Company</div>
              <input
                type="text"
                name="company"
                className="input form-control"
                value={formik.values.company}
                onChange={formik.handleChange}
              />
            </div>
          </Col>
        </Row>

        <Row>
          <Col sm={12}>
            <div className="inputBox input-group">
              <div className="inputText">Email Address</div>
              <input
                type="text"
                name="email"
                value={formik.values.email}
                onChange={formik.handleChange}
                className={
                  "input form-control " +
                  (formik.errors.email && formik.touched.email
                    ? "is-invalid"
                    : "")
                }
              />
              <div className="invalid-feedback">{formik.errors.email}</div>
            </div>
          </Col>

          <Col sm={12}>
            <div className="inputBox input-group">
              <div className="inputText">Phone Number</div>
              <input
                type="text"
                name="mobilephone"
                value={formik.values.mobilephone}
                onChange={formik.handleChange}
                className={
                  "input form-control " +
                  (formik.errors.mobilephone && formik.touched.mobilephone
                    ? "is-invalid"
                    : "")
                }
              />
              <div className="invalid-feedback">
                {formik.errors.mobilephone}
              </div>
            </div>
          </Col>
        </Row>

        <Row>
          <Col sm={12}>
            <div className="inputBox input-group">
              <div className="inputText">Message</div>
              <textarea
                className={
                  "input form-control " +
                  (formik.errors.message && formik.touched.message
                    ? "is-invalid"
                    : "")
                }
                value={formik.values.message}
                onChange={formik.handleChange}
                name="message"
              ></textarea>
              <div className="invalid-feedback">{formik.errors.message}</div>
            </div>
          </Col>
        </Row>

        <Row className="mb-2">
          <Col>
            <Recaptcha
              sitekey="6Lcm7sYUAAAAAMdo-F3LKV5n3-6aL0NLk5vzksdf"
              render="explicit"
              theme="dark"
              onloadCallback={() => console.log("recaptcha loaded")}
              verifyCallback={response => {
                formik.setFieldValue("recaptcha", response, true);
              }}
              onloadCallback={() => console.log("captacha loaded")}
            />
          </Col>
        </Row>

        <Row>
          <Col sm={12} className="d-flex justify-content-end">
            <button type="submit" className="btn btn-primary submit">
              {formik.isSubmitting ? "submitting.." : "Send"}
            </button>
          </Col>
        </Row>
        <Row>
          <Col>
            <div
              className={
                "invalid-feedback " + (formik.errors.api ? "d-block" : "")
              }
            >
              {formik.errors.api}
            </div>
          </Col>
        </Row>
      </form>
    </div>
  );
}
